import { getUiLocale } from '../../utils/uiTranslation';
import { uiText, useUiLanguage } from '../../utils/uiTranslation';
/**
 * ManpowerRequestsPanel.js
 * Surface for HR to view and manage manpower requests
 * Shows shareable link, filters, and request list
 */
import React, { useState, useEffect } from 'react';
import { ref, onValue, update } from 'firebase/database';
import { database } from '../../firebase';
import ManpowerRequestModal from './ManpowerRequestModal';
import './ManpowerRequest.css';

const ManpowerRequestsPanel = ({
    companyUID,
    onClose,
    onCreateJobPost,
    initialRequestId,
    canManageRequests = true,
    variant = 'panel'
}) => {
  useUiLanguage();
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('all');
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [hasAppliedInitial, setHasAppliedInitial] = useState(false);
    const [linkCopied, setLinkCopied] = useState(false);
    const [clearingRejected, setClearingRejected] = useState(false);

    // Settings state
    const [settings, setSettings] = useState({
        enabled: false,
        companySlug: '',
        allowedDomains: [],
        departments: [],
        locations: [],
        requireInternalApproval: false
    });

    // Load settings and requests
    useEffect(() => {
        if (!companyUID) return;

        // Load manpower request settings
        const settingsRef = ref(database, `companies/${companyUID}/settings/manpowerRequest`);
        const unsubSettings = onValue(settingsRef, (snapshot) => {
            if (snapshot.exists()) {
                setSettings(snapshot.val());
            }
        });

        // Load requests
        const requestsRef = ref(database, `companies/${companyUID}/manpower_requests`);
        const unsubRequests = onValue(requestsRef, (snapshot) => {
            const list = [];
            if (snapshot.exists()) {
                snapshot.forEach((child) => {
                    list.push({
                        id: child.key,
                        ...child.val()
                    });
                });
            }
            // Sort by newest first
            list.sort((a, b) => (b.submittedAt || 0) - (a.submittedAt || 0));
            setRequests(list);
            setLoading(false);
        });

        return () => {
            unsubSettings();
            unsubRequests();
        };
    }, [companyUID]);

    useEffect(() => {
        setHasAppliedInitial(false);
    }, [initialRequestId]);

    useEffect(() => {
        if (!initialRequestId) {
            setHasAppliedInitial(false);
            return;
        }
        if (hasAppliedInitial) return;
        const match = requests.find((req) => req.id === initialRequestId);
        if (match) {
            setSelectedRequest(match);
            setHasAppliedInitial(true);
        }
    }, [initialRequestId, hasAppliedInitial, requests]);

    const visibleRequests = settings.requireInternalApproval
        ? requests.filter((req) => req.status !== 'pending_internal')
        : requests;

    // Filter requests
    const filteredRequests = visibleRequests.filter((req) => {
        if (filter === 'all') return true;
        return req.status === filter;
    });

    // Build shareable URL
    const shareableUrl = settings.companySlug
        ? `${window.location.origin}/request-hire/${settings.companySlug}`
        : '';

    // Copy link handler
    const handleCopyLink = () => {
        if (!shareableUrl) return;
        navigator.clipboard.writeText(shareableUrl);
        setLinkCopied(true);
        setTimeout(() => setLinkCopied(false), 2000);
    };

    // Format date
    const formatDate = (timestamp) => {
        if (!timestamp) return '';
        const date = new Date(timestamp);
        return date.toLocaleDateString(getUiLocale(), {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
        });
    };

    // Get status badge class
    const getStatusClass = (status) => {
        switch (status) {
            case 'pending_internal': return 'pending-internal';
            case 'pending_hr': return 'pending-hr';
            case 'approved': return 'approved';
            case 'rejected': return 'rejected';
            case 'fulfilled': return 'fulfilled';
            default: return 'pending-hr';
        }
    };

    // Get status label
    const getStatusLabel = (status) => {
        switch (status) {
            case 'pending_internal': return uiText('Awaiting Finance/GM Approval');
            case 'pending_hr': return uiText('Pending HR');
            case 'approved': return uiText('Approved');
            case 'rejected': return uiText('Rejected');
            case 'fulfilled': return uiText('Fulfilled');
            default: return uiText('Pending');
        }
    };

    // Get urgency label
    const getUrgencyLabel = (urgency) => {
        switch (urgency) {
            case 'immediate': return uiText('Immediate');
            case '30_days': return uiText('30 Days');
            case '90_days': return uiText('90 Days');
            case 'not_urgent': return uiText('Not Urgent');
            default: return urgency || '';
        }
    };

    // Handle request approval
    const handleApprove = async (request) => {
        if (!canManageRequests) {
            alert(uiText('You do not have permission to manage manpower requests.'));
            return;
        }

        try {
            await update(ref(database, `companies/${companyUID}/manpower_requests/${request.id}`), {
                status: 'approved',
                reviewedAt: Date.now()
            });
            setSelectedRequest(null);
        } catch (err) {
            console.error('Error approving request:', err);
            alert(uiText('Failed to approve request.'));
        }
    };

    // Handle request rejection
    const handleReject = async (request, reason) => {
        if (!canManageRequests) {
            alert(uiText('You do not have permission to manage manpower requests.'));
            return;
        }

        try {
            await update(ref(database, `companies/${companyUID}/manpower_requests/${request.id}`), {
                status: 'rejected',
                reviewedAt: Date.now(),
                rejectionReason: reason || 'No reason provided'
            });
            setSelectedRequest(null);
        } catch (err) {
            console.error('Error rejecting request:', err);
            alert(uiText('Failed to reject request.'));
        }
    };

    // Handle create job from request
    const handleCreateJob = (request) => {
        if (!canManageRequests) {
            alert(uiText('You do not have permission to create job posts from manpower requests.'));
            return;
        }

        if (onCreateJobPost) {
            onCreateJobPost(request);
        }
        setSelectedRequest(null);
    };

    // Handle manual fulfillment (hired outside ATS)
    const handleMarkFulfilled = async (request, notes) => {
        if (!canManageRequests) {
            alert(uiText('You do not have permission to manage manpower requests.'));
            return;
        }

        try {
            await update(ref(database, `companies/${companyUID}/manpower_requests/${request.id}`), {
                status: 'fulfilled',
                fulfilledAt: Date.now(),
                fulfillmentNotes: notes || 'Marked as fulfilled manually',
                linkedJobPostId: null // Not linked to a job post
            });
            setSelectedRequest(null);
            // TODO: Send notification email to request submitter
        } catch (err) {
            console.error('Error marking request as fulfilled:', err);
            alert(uiText('Failed to mark request as fulfilled.'));
        }
    };

    const handleClearRejected = async () => {
        if (!canManageRequests) {
            alert(uiText('You do not have permission to manage manpower requests.'));
            return;
        }

        const rejectedRequests = visibleRequests.filter((request) => request.status === 'rejected');
        if (rejectedRequests.length === 0 || !companyUID) return;

        if (!window.confirm(`Clear ${rejectedRequests.length} rejected request${rejectedRequests.length === 1 ? '' : 's'}?`)) {
            return;
        }

        setClearingRejected(true);
        try {
            const updates = {};
            rejectedRequests.forEach((request) => {
                updates[`companies/${companyUID}/manpower_requests/${request.id}`] = null;
            });
            await update(ref(database), updates);
            if (filter === 'rejected') {
                setFilter('all');
            }
        } catch (err) {
            console.error('Error clearing rejected requests:', err);
            alert(uiText('Failed to clear rejected requests.'));
        } finally {
            setClearingRejected(false);
        }
    };

    // Count by status
    const countByStatus = (status) => {
        if (status === 'all') return visibleRequests.length;
        return visibleRequests.filter(r => r.status === status).length;
    };

    const pendingInternalCount = requests.filter(r => r.status === 'pending_internal').length;
    const isPage = variant === 'page';
    const rejectedCount = countByStatus('rejected');
    const statusFilters = [
        { key: 'all', label: 'All' },
        { key: 'pending_hr', label: 'Pending' },
        { key: 'approved', label: 'Approved' },
        { key: 'rejected', label: 'Rejected' },
        { key: 'fulfilled', label: 'Fulfilled' }
    ];

    const content = (
        <div className={isPage ? 'manpower-panel manpower-panel-page' : 'manpower-panel'} onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="manpower-panel-header">
                <div>
                    <h2>{uiText("Manpower Requests")}</h2>
                </div>
                {!isPage && (
                    <button className="manpower-panel-close" onClick={onClose}>
                        <i className="fa-solid fa-times" />
                    </button>
                )}
            </div>

            {/* Shareable Link */}
            {settings.enabled && shareableUrl && (
                <div className="manpower-link-section">
                    <label>{uiText("Request form URL")}</label>
                    <p className="manpower-link-note">{uiText("Share this with managers to submit requests.")}</p>
                    <div className="manpower-link-box">
                        <input
                            type="text"
                            className="manpower-link-input"
                            value={shareableUrl}
                            readOnly
                        />
                        <button
                            className="manpower-link-copy"
                            onClick={handleCopyLink}
                        >
                            <i className={`fa-solid ${linkCopied ? 'fa-check' : 'fa-copy'}`} />
                            {linkCopied ? uiText("Copied!") : uiText("Copy")}
                        </button>
                    </div>
                </div>
            )}

            {/* Not Enabled Message */}
            {!settings.enabled && (
                <div className="manpower-link-section">
                    <p className="manpower-link-disabled">{uiText("Manpower request form is disabled.")}</p>
                </div>
            )}

            {/* Filters */}
            <div className="manpower-panel-filters">
                {statusFilters.map((f) => (
                    <button
                        key={f.key}
                        className={`manpower-filter-btn ${filter === f.key ? 'active' : ''}`}
                        onClick={() => setFilter(f.key)}
                    >
                        {uiText(f.label)} ({countByStatus(f.key)})
                    </button>
                ))}
                {rejectedCount > 0 && (
                    <button
                        type="button"
                        className="manpower-clear-rejected-btn"
                        onClick={handleClearRejected}
                        disabled={clearingRejected}
                    >
                        <i className={`fa-solid ${clearingRejected ? 'fa-spinner fa-spin' : 'fa-trash'}`} />
                        {clearingRejected ? uiText("Clearing...") : uiText("Clear rejected ({{count}})", { count: rejectedCount })}
                    </button>
                )}
            </div>

            {settings.requireInternalApproval && pendingInternalCount > 0 && (
                <div className="manpower-internal-approval-note">{uiText("Awaiting Finance/GM approval (")}{pendingInternalCount})
                </div>
            )}

            {/* Request List */}
            <div className="manpower-panel-list">
                {loading ? (
                    <div className="manpower-panel-empty">
                        <i className="fa-solid fa-spinner fa-spin" />
                        <h3>{uiText("Loading...")}</h3>
                    </div>
                ) : filteredRequests.length === 0 ? (
                    <div className="manpower-panel-empty">
                        <i className="fa-solid fa-inbox" />
                        <h3>{uiText("No Requests")}</h3>
                        <p>
                            {filter === 'all'
                                ? uiText("No manpower requests have been submitted yet.")
                                : uiText("No {{status}} requests.", { status: getStatusLabel(filter).toLowerCase() })}
                        </p>
                    </div>
                ) : (
                    filteredRequests.map((request) => (
                        <button
                            type="button"
                            key={request.id}
                            className="manpower-request-cell"
                            onClick={() => setSelectedRequest(request)}
                        >
                            <div className="manpower-request-header">
                                <div>
                                    <h4 className="manpower-request-title">
                                        {request.position?.title || uiText("Untitled Position")}
                                    </h4>
                                    {request.position?.headcount > 1 && (
                                        <span className="manpower-request-headcount">
                                            × {request.position.headcount}{uiText(" positions")}</span>
                                    )}
                                </div>
                            </div>
                            <span className={`manpower-status-badge ${getStatusClass(request.status)}`}>
                                {uiText(getStatusLabel(request.status))}
                            </span>
                            <div className="manpower-request-meta">
                                <span>
                                    <i className="fa-solid fa-user" />
                                    {request.submitter?.name || uiText("Unknown")}
                                </span>
                                <span>
                                    <i className="fa-solid fa-building" />
                                    {request.submitter?.department || uiText("No department")}
                                </span>
                                {request.position?.urgency && (
                                    <span>
                                        <i className="fa-solid fa-clock" />
                                        {uiText(getUrgencyLabel(request.position.urgency))}
                                    </span>
                                )}
                                <span>
                                    <i className="fa-solid fa-calendar" />
                                    {formatDate(request.submittedAt)}
                                </span>
                            </div>
                            <i className="fa-solid fa-chevron-right manpower-request-arrow" />
                        </button>
                    ))
                )}
            </div>
        </div>
    );

    return (
        <>
            {isPage ? content : (
                <div className="manpower-panel-overlay" onClick={onClose}>
                    {content}
                </div>
            )}

            {/* Request Detail Modal */}
            {selectedRequest && (
                <ManpowerRequestModal
                    request={selectedRequest}
                    onClose={() => setSelectedRequest(null)}
                    onApprove={() => handleApprove(selectedRequest)}
                    onReject={(reason) => handleReject(selectedRequest, reason)}
                    onCreateJob={() => handleCreateJob(selectedRequest)}
                    onMarkFulfilled={(notes) => handleMarkFulfilled(selectedRequest, notes)}
                    canManageRequests={canManageRequests}
                />
            )}
        </>
    );
};

export default ManpowerRequestsPanel;
